import { Message, MessageContent } from "@/components/ai-elements/message";
import { MessageResponseWithBlocks } from "@/components/ai-elements/message-response-with-blocks";
import {
  LLM_PROVIDER_LOGOS,
  type LLMProvider,
} from "@/components/team/llm-permissions/llm-provider-logos";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/utils/time";
import { UIMessage } from "ai";
import { BrainCogIcon } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { TbCircleFilled } from "react-icons/tb";
import TextareaAutosize from "react-textarea-autosize";

/**
 * Shared chat dialog primitives.
 *
 * Single source of truth for how an AI conversation looks, reused on every
 * surface where the user talks to the AI (main chat, team instruction
 * conversation, …) so the rendering and classes stay identical without
 * dragging the full chat logic into each flow.
 */

const MS_IN_S = 1000;

/**
 * The vertical gap of a conversation, between the turns it stacks.
 *
 * Owned by the container that stacks them: an element must never carry its own
 * margin, or the gap around it stops matching the gap around its neighbours.
 * Hover-only trailers (the meta footer, a bubble's copy/edit row) are overlaid
 * into this gap rather than taking flow height, which is what made the spacing
 * depend on which element came before.
 */
export const CHAT_STACK_GAP_CLASS = "space-y-7";

/**
 * The gap between the parts *inside* one turn — its prose, its research group,
 * its tool cards.
 *
 * Tighter than the gap between turns, and that is the whole point: given the
 * same one, an answer made of a sentence, a card and another sentence reads as
 * three separate turns.
 */
export const CHAT_MESSAGE_PART_GAP_CLASS = "space-y-3";

/**
 * The gap between the last turn and the quick replies hanging off it, when they
 * render inside the message — the normal case, which is also the only way the
 * hover-only meta footer stays readable: hung off the message's bottom edge, it
 * would otherwise print underneath the chips.
 *
 * They belong to the message above them, so they sit closer to it than a turn
 * would — the width of one gap between elements of the same answer.
 */
export const CHAT_QUICK_REPLIES_GAP_CLASS = "mt-2";

/**
 * The same gap, for the one case where the replies cannot live inside the turn
 * they belong to: a thread ending on a clarification run, whose card is built
 * from several messages and has no single message to hang them off. There they
 * are a sibling in the stack, so the stack's own gap has to be pulled back.
 */
export const CHAT_QUICK_REPLIES_STANDALONE_GAP_CLASS = "!-mt-5";

/** Seconds elapsed since `startedAtMs`, ticking every second. */
function useElapsedSeconds(startedAtMs: number): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    setNowMs(Date.now());
    const interval = setInterval(() => setNowMs(Date.now()), MS_IN_S);
    return () => clearInterval(interval);
  }, [startedAtMs]);

  return Math.max(0, Math.round((nowMs - startedAtMs) / MS_IN_S));
}

/** Running time of the turn in progress, counting up from the question. */
export function ChatElapsedTime({
  startedAtMs,
}: {
  startedAtMs: number;
}): React.JSX.Element {
  const elapsedSeconds = useElapsedSeconds(startedAtMs);

  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      {formatDuration(elapsedSeconds)}
    </span>
  );
}

/**
 * Pulsing dot rendered while an assistant message is still streaming, followed
 * by the turn's running time when the moment the question was asked is known.
 */
export function ChatStreamingIndicator({
  className,
  startedAtMs,
}: {
  className?: string;
  /** Start of the turn being streamed; omit to show the dot alone. */
  startedAtMs?: number;
}): React.JSX.Element {
  return (
    <span className="inline-flex gap-2 items-center">
      <TbCircleFilled
        className={cn("inline-block animate-pulse text-majorelle", className)}
        size={14}
      />
      {startedAtMs !== undefined && <ChatElapsedTime startedAtMs={startedAtMs} />}
    </span>
  );
}

/** Renders a single assistant text part as markdown with block support. */
export function ChatAssistantText({
  children,
  isStreaming = false,
}: {
  children: string;
  isStreaming?: boolean;
}): React.JSX.Element {
  return (
    <Message from="assistant" className="max-w-full">
      <MessageContent>
        <MessageResponseWithBlocks isStreaming={isStreaming}>
          {children}
        </MessageResponseWithBlocks>
      </MessageContent>
    </Message>
  );
}

type ChatAssistantMessageContentProps = {
  messageId: string;
  parts: UIMessage["parts"];
  isStreaming: boolean;
  /** Optional transform applied to each text part before rendering (e.g. stripping tags). */
  transformText?: (text: string) => string;
};

/**
 * Renders the text parts of an assistant message full-width, appending a
 * streaming indicator while the response is in progress. Non-text parts
 * (reasoning, tools, …) are ignored — surfaces that need them render their own.
 */
export function ChatAssistantMessageContent({
  messageId,
  parts,
  isStreaming,
  transformText,
}: ChatAssistantMessageContentProps): React.JSX.Element {
  return (
    <div className="overflow-hidden w-full">
      {parts.map((part, i) => {
        if (part.type !== "text") return null;
        const text = transformText ? transformText(part.text) : part.text;
        if (!text) return null;
        return (
          <ChatAssistantText key={`${messageId}-${i}`} isStreaming={isStreaming}>
            {text}
          </ChatAssistantText>
        );
      })}
      {isStreaming && <ChatStreamingIndicator />}
    </div>
  );
}

export type ChatAssistantMetaInfo = {
  llmId?: string;
  createdAt?: string | Date;
};

/**
 * Footer under an assistant message showing which model generated it, when, and
 * how long the whole turn took. Revealed on hover of the enclosing `group`
 * element, which must be positioned: the footer is overlaid into the gap below
 * the message so a message with a model badge is not spaced differently from
 * one without. It stays shown while one of its pills has its overlay open: the
 * pointer leaves the message to reach a popover, and the pill it hangs from
 * would fade out under it.
 */
export function ChatAssistantMeta({
  llmId,
  createdAt,
  generationDurationMs,
  children,
}: ChatAssistantMetaInfo & {
  /** Reasoning + search + writing, end to end. Omitted when unknown. */
  generationDurationMs?: number;
  /** Pills about the answer itself (its sources), set right of the model's. */
  children?: ReactNode;
}): React.JSX.Element | null {
  const { t, currentLanguage } = useTranslation();
  const { teamLLMList } = useTeams();

  if (!llmId && !createdAt && !children) return null;

  const getLLMDisplayName = (id: string): string => {
    const model = teamLLMList.find((llm) => llm.id === id);
    if (model) return model.name;
    // Fallback: extract readable name from ID (e.g., "openai/gpt-4o" → "gpt-4o")
    return id.split("/").pop() || id;
  };

  const getLLMLogo = (id: string) => {
    const model = teamLLMList.find((llm) => llm.id === id);
    // Fallback on the provider prefix of the ID (e.g. "openai/gpt-4o" →
    // "openai") so a message written by a retired model, absent from
    // `teamLLMList`, still shows its provider rather than the generic icon.
    const providerId = model?.groupId ?? id.split("/")[0];
    return LLM_PROVIDER_LOGOS[providerId as LLMProvider];
  };

  return (
    <div className="flex absolute left-0 top-full items-center gap-2 pt-1 opacity-0 transition-opacity group-hover:opacity-100 touch:opacity-100 has-data-[state=open]:opacity-100">
      {llmId && (
        <Tooltip delayDuration={0}>
          <TooltipTrigger>
            <div className="inline-flex gap-1 items-center px-2 py-0.5 text-xs rounded-full border text-muted-foreground">
              {getLLMLogo(llmId) ? (
                <img src={getLLMLogo(llmId)} alt="" className="w-3 h-3" />
              ) : (
                <BrainCogIcon className="w-3 h-3" />
              )}
              {getLLMDisplayName(llmId)}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {t("chat.message.llm.tooltip", {
              llmName: getLLMDisplayName(llmId),
            })}
          </TooltipContent>
        </Tooltip>
      )}
      {children}
      {createdAt && (
        <div className="text-xs text-muted-foreground">
          {t("chat.message.generatedAt", {
            date: new Date(createdAt).toLocaleString(currentLanguage),
          })}
        </div>
      )}
      {generationDurationMs !== undefined && (
        <>
          <span className="text-xs text-muted-foreground/40">&middot;</span>
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="text-xs text-muted-foreground">
              {formatDuration(Math.round(generationDurationMs / MS_IN_S))}
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t("chat.message.generationDuration.tooltip")}
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  );
}

/** Right-aligned majorelle bubble wrapping the text of a user message. */
export function ChatUserMessageBubble({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="px-4 py-2.5 text-base leading-relaxed whitespace-pre-line rounded-2xl rounded-br-sm bg-majorelle text-majorelle-foreground">
      {children}
    </div>
  );
}

/** Row wrapper that right-aligns a user message bubble. */
export function ChatUserMessageRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div className="flex justify-end w-full">
      <div className={cn("flex flex-col gap-1.5 items-end max-w-[80%]", className)}>
        {children}
      </div>
    </div>
  );
}

/** Bordered frame wrapping a conversation composer (textarea + toolbar). */
export function ChatComposerFrame({
  className,
  ...props
}: ComponentProps<"div">): React.JSX.Element {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border bg-background transition-all",
        className
      )}
      data-slot="chat-composer-frame"
      {...props}
    />
  );
}

/**
 * Gray tray tucked behind the composer's rounded bottom corners (-mt-4 == the
 * frame's rounded-2xl radius) so it fills the corner notches, carrying the AI
 * disclaimer. Its height is what holds a docked composer above the bottom edge,
 * so the new-conversation glide measures this very element to know how far the
 * composer has to travel — both surfaces must render it identically.
 */
export const ChatComposerDisclaimer = forwardRef<
  HTMLDivElement,
  { className?: string }
>(function ChatComposerDisclaimer({ className }, ref): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className={cn("-mt-4 pt-7 pb-3 compact:pt-6 compact:pb-2 bg-muted", className)}
    >
      <p className="px-3 text-xs text-center text-muted-foreground">
        {t("chat.conversation.disclaimer")}
      </p>
    </div>
  );
});

export type ChatComposerTextareaProps = ComponentProps<typeof TextareaAutosize>;

/** Auto-sizing composer textarea that submits its form on Enter (Shift+Enter for newline). */
export const ChatComposerTextarea = forwardRef<
  HTMLTextAreaElement,
  ChatComposerTextareaProps
>(function ChatComposerTextarea(
  { className, minRows = 2, maxRows = 15, onKeyDown, ...props },
  ref
) {
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <TextareaAutosize
      ref={ref}
      minRows={minRows}
      maxRows={maxRows}
      onKeyDown={handleKeyDown}
      className={cn(
        "px-3 pt-3 pb-0 w-full text-base bg-transparent border-0 shadow-none resize-none focus:outline-none",
        className
      )}
      {...props}
    />
  );
});

/** Bottom toolbar row inside a composer frame. */
export function ChatComposerToolbar({
  className,
  ...props
}: ComponentProps<"div">): React.JSX.Element {
  return (
    <div
      className={cn("flex items-center gap-2 p-3 pt-0", className)}
      data-slot="chat-composer-toolbar"
      {...props}
    />
  );
}
